import React from "react";
import { Button, Drawer, Text } from "braid-design-system";
import { CustomerReceipt, getCustomerReceipts } from "../../services/accountPaymentService";

type CustomerReceiptHistoryViewerProps = {
    // Bump to reload the last 10 receipts, e.g. after a save.
    refreshKey: number;
    onReprint: (receipt: CustomerReceipt) => void;
};

const RECENT_LIMIT = 10;
const SEARCH_PAGE_SIZE = 25;

const formatAmount = (value: number): string =>
    (Number.isFinite(value) ? value : 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

// created_at is the server's local save time without a zone, which Date parses as local time.
export const formatReceiptDate = (value: string): string => {
    if (!value) {
        return "-";
    }

    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? value : parsed.toLocaleString();
};

const getReceiptKey = (receipt: CustomerReceipt): string =>
    `${receipt.customer_code}|${receipt.created_at}|${receipt.docnos.join(",")}`;

const getDocNoSummary = (receipt: CustomerReceipt): string => {
    const [first, ...rest] = receipt.docnos;
    if (!first) {
        return "-";
    }

    return rest.length ? `${first} +${rest.length} more` : first;
};

const getCustomerDisplay = (receipt: CustomerReceipt): string =>
    receipt.customer_name ? `${receipt.customer_code} - ${receipt.customer_name}` : receipt.customer_code;

const CustomerReceiptHistoryViewer: React.FC<CustomerReceiptHistoryViewerProps> = ({ refreshKey, onReprint }) => {
    const [recentReceipts, setRecentReceipts] = React.useState<CustomerReceipt[]>([]);
    const [recentError, setRecentError] = React.useState<string | null>(null);
    const [isOpen, setIsOpen] = React.useState(false);
    const [isSearchMode, setIsSearchMode] = React.useState(false);
    const [searchText, setSearchText] = React.useState("");
    const [dateFrom, setDateFrom] = React.useState("");
    const [dateTo, setDateTo] = React.useState("");
    const [searchOffset, setSearchOffset] = React.useState(0);
    const [searchResults, setSearchResults] = React.useState<CustomerReceipt[]>([]);
    const [isSearching, setIsSearching] = React.useState(false);
    const [searchError, setSearchError] = React.useState<string | null>(null);
    const [hasSearched, setHasSearched] = React.useState(false);
    const [selectedReceipt, setSelectedReceipt] = React.useState<CustomerReceipt | null>(null);
    const latestReceipt = recentReceipts[0] ?? null;
    const displayedReceipts = isSearchMode ? searchResults : recentReceipts;
    const tableEmptyMessage = isSearchMode
        ? hasSearched ? "No matching customer receipts found." : "Use the filters above to search older receipts."
        : recentError || "No recent customer receipts available.";

    React.useEffect(() => {
        let mounted = true;

        getCustomerReceipts({ limit: RECENT_LIMIT })
            .then((receipts) => {
                if (mounted) {
                    setRecentReceipts(receipts);
                    setRecentError(null);
                }
            })
            .catch((error: any) => {
                if (mounted) {
                    setRecentReceipts([]);
                    setRecentError(error?.message || "Failed to load customer receipts.");
                }
            });

        return () => {
            mounted = false;
        };
    }, [refreshKey]);

    const executeSearch = async (nextOffset: number = 0) => {
        try {
            setIsSearching(true);
            setSearchError(null);
            const results = await getCustomerReceipts({
                q: searchText.trim() || undefined,
                date_from: dateFrom || undefined,
                date_to: dateTo || undefined,
                limit: SEARCH_PAGE_SIZE,
                offset: nextOffset,
            });
            setSearchResults(results);
            setSearchOffset(nextOffset);
            setHasSearched(true);
        } catch (error: any) {
            setSearchError(error?.message || "Failed to search customer receipts.");
            setSearchResults([]);
            setHasSearched(true);
        } finally {
            setIsSearching(false);
        }
    };

    const openSearchMode = () => {
        setIsSearchMode(true);
        setSelectedReceipt(null);
        if (!hasSearched) {
            executeSearch(0);
        }
    };

    const resetSearch = () => {
        setSearchText("");
        setDateFrom("");
        setDateTo("");
        setSearchOffset(0);
        setSearchResults([]);
        setSearchError(null);
        setHasSearched(false);
        setSelectedReceipt(null);
    };

    const openReceipt = (receipt: CustomerReceipt) => {
        setSelectedReceipt(receipt);
        setIsOpen(true);
    };

    return (
        <section className="accounts-history-viewer" aria-label="Customer receipt history viewer">
            {latestReceipt ? (
                <div className="accounts-history-summary">
                    <button
                        type="button"
                        className="accounts-history-link"
                        onClick={() => openReceipt(latestReceipt)}
                    >
                        Last receipt: {getDocNoSummary(latestReceipt)} ({latestReceipt.customer_code}) {formatAmount(latestReceipt.amount)} on {formatReceiptDate(latestReceipt.created_at)}
                    </button>
                    <span className="accounts-history-separator" aria-hidden="true">-&gt;</span>
                    <button
                        type="button"
                        className="accounts-history-list-link"
                        onClick={() => {
                            setSelectedReceipt(null);
                            setIsOpen(true);
                        }}
                    >
                        list last 10
                    </button>
                </div>
            ) : (
                <div className="accounts-history-summary">
                    <span className="accounts-history-empty">{recentError || "No recent customer receipts found."}</span>
                    <button
                        type="button"
                        className="accounts-history-list-link"
                        onClick={() => {
                            setSelectedReceipt(null);
                            setIsOpen(true);
                        }}
                    >
                        open history
                    </button>
                </div>
            )}

            <Drawer
                title="Customer Receipts List View"
                open={isOpen}
                onClose={() => setIsOpen(false)}
                width="large"
            >
                <div className="accounts-history-drawer">
                    <div className="accounts-history-drawer-heading">
                        <Text tone="secondary">Saved customer receipts in this branch</Text>
                        <div className="accounts-history-mode-actions">
                            <Button
                                size="small"
                                variant={isSearchMode ? "ghost" : "solid"}
                                tone="brandAccent"
                                onClick={() => {
                                    setIsSearchMode(false);
                                    setSelectedReceipt(null);
                                }}
                            >
                                Last 10
                            </Button>
                            <Button
                                size="small"
                                variant={isSearchMode ? "solid" : "ghost"}
                                tone="brandAccent"
                                onClick={openSearchMode}
                            >
                                Search older receipts
                            </Button>
                        </div>
                    </div>

                    {isSearchMode ? (
                        <form
                            className="accounts-history-search"
                            onSubmit={(event) => {
                                event.preventDefault();
                                setSelectedReceipt(null);
                                executeSearch(0);
                            }}
                        >
                            <label className="accounts-history-search-field accounts-history-search-field-wide">
                                <span>Receipt, invoice, customer, or payer</span>
                                <input
                                    value={searchText}
                                    onChange={(event) => setSearchText(event.target.value)}
                                    placeholder="Search receipt or invoice number"
                                />
                            </label>
                            <label className="accounts-history-search-field">
                                <span>From</span>
                                <input type="date" value={dateFrom} onChange={(event) => setDateFrom(event.target.value)} />
                            </label>
                            <label className="accounts-history-search-field">
                                <span>To</span>
                                <input type="date" value={dateTo} onChange={(event) => setDateTo(event.target.value)} />
                            </label>
                            <div className="accounts-history-search-actions">
                                <Button type="submit" size="small" variant="solid" tone="brandAccent" loading={isSearching}>
                                    Search
                                </Button>
                                <Button type="button" size="small" variant="ghost" onClick={resetSearch}>
                                    Clear
                                </Button>
                            </div>
                        </form>
                    ) : null}

                    {searchError ? (
                        <div className="accounts-history-search-error">{searchError}</div>
                    ) : null}

                    {selectedReceipt ? (
                        <div className="accounts-history-detail" aria-label="Receipt details">
                            <div className="accounts-history-detail-heading">
                                <Text weight="strong">Receipt {selectedReceipt.docnos.join(", ") || "-"}</Text>
                                <div className="accounts-history-mode-actions">
                                    <Button size="small" variant="solid" tone="brandAccent" onClick={() => onReprint(selectedReceipt)}>
                                        Reprint
                                    </Button>
                                    <Button size="small" variant="ghost" onClick={() => setSelectedReceipt(null)}>
                                        Close
                                    </Button>
                                </div>
                            </div>
                            <dl className="accounts-history-detail-grid">
                                {[
                                    ["Customer", getCustomerDisplay(selectedReceipt)],
                                    ["Date", formatReceiptDate(selectedReceipt.created_at)],
                                    ["Amount", formatAmount(selectedReceipt.amount)],
                                    ["Payment Mode", selectedReceipt.payment_mode],
                                    ["Cheque No", selectedReceipt.cheque_no],
                                    ["Cheque Date", selectedReceipt.cheque_date],
                                    ["Payer Name", selectedReceipt.payer_name],
                                    ["Reference No", selectedReceipt.reference_no],
                                    ["User", selectedReceipt.user_name],
                                    ["Location", selectedReceipt.location_label],
                                    ["Note", selectedReceipt.note],
                                ].map(([label, value]) => (
                                    <div key={label} className="accounts-history-detail-field">
                                        <dt>{label}</dt>
                                        <dd>{value || "-"}</dd>
                                    </div>
                                ))}
                            </dl>
                            <div className="accounts-history-table-wrap">
                                <table className="accounts-history-table">
                                    <thead>
                                        <tr>
                                            <th>Receipt No</th>
                                            <th>Invoice No</th>
                                            <th>Type</th>
                                            <th className="accounts-history-amount">Invoice Amount</th>
                                            <th className="accounts-history-amount">Allocated Amount</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {selectedReceipt.lines.map((line, index) => (
                                            <tr key={`${line.docno}-${index}`}>
                                                <td>{line.docno}</td>
                                                <td>{line.invoice_no || "-"}</td>
                                                <td>{line.description || "-"}</td>
                                                <td className="accounts-history-amount">{formatAmount(line.invoice_amount)}</td>
                                                <td className="accounts-history-amount">{formatAmount(line.allocated_amount)}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    ) : null}

                    <div className="accounts-history-table-wrap">
                        <table className="accounts-history-table">
                            <thead>
                                <tr>
                                    <th>Receipt No</th>
                                    <th>Customer</th>
                                    <th className="accounts-history-amount">Amount</th>
                                    <th>Receipt Date</th>
                                    <th></th>
                                </tr>
                            </thead>
                            <tbody>
                                {displayedReceipts.length > 0 ? (
                                    displayedReceipts.map((receipt) => {
                                        const receiptKey = getReceiptKey(receipt);
                                        const isSelected = selectedReceipt ? getReceiptKey(selectedReceipt) === receiptKey : false;

                                        return (
                                            <tr key={receiptKey} className={isSelected ? "is-selected" : undefined}>
                                                <td>
                                                    <button
                                                        type="button"
                                                        className="accounts-history-table-link"
                                                        title={receipt.docnos.join(", ")}
                                                        onClick={() => setSelectedReceipt(receipt)}
                                                    >
                                                        {getDocNoSummary(receipt)}
                                                    </button>
                                                </td>
                                                <td>{getCustomerDisplay(receipt)}</td>
                                                <td className="accounts-history-amount">{formatAmount(receipt.amount)}</td>
                                                <td>{formatReceiptDate(receipt.created_at)}</td>
                                                <td>
                                                    <div className="accounts-history-actions">
                                                        <Button size="small" variant="ghost" onClick={() => setSelectedReceipt(receipt)}>
                                                            View
                                                        </Button>
                                                        <Button size="small" variant="ghost" onClick={() => onReprint(receipt)}>
                                                            Reprint
                                                        </Button>
                                                    </div>
                                                </td>
                                            </tr>
                                        );
                                    })
                                ) : (
                                    <tr>
                                        <td colSpan={5} className="accounts-history-empty-cell">
                                            {tableEmptyMessage}
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                    {isSearchMode ? (
                        <div className="accounts-history-pagination">
                            <Button
                                size="small"
                                variant="ghost"
                                onClick={searchOffset <= 0 || isSearching ? undefined : () => executeSearch(Math.max(0, searchOffset - SEARCH_PAGE_SIZE))}
                            >
                                Previous
                            </Button>
                            <span>Showing {displayedReceipts.length ? `${searchOffset + 1}-${searchOffset + displayedReceipts.length}` : "0"}</span>
                            <Button
                                size="small"
                                variant="ghost"
                                onClick={displayedReceipts.length < SEARCH_PAGE_SIZE || isSearching ? undefined : () => executeSearch(searchOffset + SEARCH_PAGE_SIZE)}
                            >
                                Next
                            </Button>
                        </div>
                    ) : null}
                </div>
            </Drawer>
        </section>
    );
};

export default CustomerReceiptHistoryViewer;
